// Fix "nested" back navigation after returning from Stripe Checkout.
// Problem: Stripe -> return_url creates history like [in-app] -> Stripe -> [in-app],
// so pressing back from the return page goes back into Stripe again.
//
// Approach:
// 1) Before leaving to Stripe, mark the current in-app route in sessionStorage.
// 2) After returning, jump back two entries (to skip Stripe) and then navigate back to the
//    return route. If the return route is the same page as the "from" page, we use replace()
//    to collapse duplicates; otherwise we use push() so the user can still go back to "from".
const KEY_FROM = '__stripe_from_fullPath'
const KEY_TS = '__stripe_from_ts'
const KEY_FIX_PENDING = '__stripe_fix_pending'
const KEY_RETURN_TO = '__stripe_return_to_fullPath'
const KEY_NAV_MODE = '__stripe_nav_mode'
const MAX_AGE_MS = 10 * 60 * 1000
function safeGetSessionStorage() {
  try {
    return window.sessionStorage
  } catch (e) {
    return null
  }
}
function isLikelyFromStripeReferrer() {
  try {
    const ref = String(document.referrer || '')
    // Match stripe.com (with or without subdomain prefix)
    return /(^|\.)stripe\.com\b/i.test(ref)
  } catch (e) {
    return false
  }
}
function now() {
  return Date.now ? Date.now() : new Date().getTime()
}
function getCurrentFullPathFromHash() {
  // VueRouter is in hash mode; hash is like "#/order?x=1"
  const hash = String(window.location.hash || '')
  const withoutHash = hash.startsWith('#') ? hash.slice(1) : hash
  return withoutHash || '/'
}
export function markStripeRedirectStart() {
  if (typeof window === 'undefined') return
  const ss = safeGetSessionStorage()
  if (!ss) return
  try {
    ss.setItem(KEY_FROM, getCurrentFullPathFromHash())
    ss.setItem(KEY_TS, String(now()))
  } catch (e) {
    // ignore
  }
}
function clearAll(ss) {
  try { ss.removeItem(KEY_FROM) } catch (e) {}
  try { ss.removeItem(KEY_TS) } catch (e) {}
  try { ss.removeItem(KEY_FIX_PENDING) } catch (e) {}
  try { ss.removeItem(KEY_RETURN_TO) } catch (e) {}
  try { ss.removeItem(KEY_NAV_MODE) } catch (e) {}
}
function getPathOnly(fullPath) {
  return String(fullPath || '').split('?')[0] || '/'
}
// Call from router.afterEach().
export function maybeFixStripeNestedHistory(router) {
  if (typeof window === 'undefined') return
  const ss = safeGetSessionStorage()
  if (!ss) return
  const pending = ss.getItem(KEY_FIX_PENDING)
  const returnTo = ss.getItem(KEY_RETURN_TO)
  const navMode = ss.getItem(KEY_NAV_MODE) || 'replace'
  // Step 2: after we jumped back, navigate to the original return route.
  if (pending && returnTo) {
    try {
      ss.removeItem(KEY_FIX_PENDING)
      ss.removeItem(KEY_RETURN_TO)
      ss.removeItem(KEY_NAV_MODE)
      ss.removeItem(KEY_FROM)
      ss.removeItem(KEY_TS)
    } catch (e) {
      // ignore
    }
    if (router && router.currentRoute && router.currentRoute.fullPath !== returnTo) {
      const nav = navMode === 'push' ? router.push : router.replace
      nav.call(router, returnTo).catch(() => {})
    }
    return
  }
  const from = ss.getItem(KEY_FROM)
  const ts = Number(ss.getItem(KEY_TS) || 0)
  if (!from) return
  if (ts && now() - ts > MAX_AGE_MS) {
    clearAll(ss)
    return
  }
  // Only apply the fix right after returning from Stripe.
  // NOTE: Skip gift card payments - they have their own handling in buy.vue
  const hasPayQuery =
    router &&
    router.currentRoute &&
    router.currentRoute.query &&
    (
      router.currentRoute.query.pay === 'success' ||
      router.currentRoute.query.pay === 'cancel'
    )
  // Gift card payments are handled separately in buy.vue, don't interfere
  const isGiftCardPay =
    router &&
    router.currentRoute &&
    router.currentRoute.query &&
    (
      router.currentRoute.query.giftcard_pay === 'success' ||
      router.currentRoute.query.giftcard_pay === 'cancel'
    )
  // If gift card payment, clear all markers and return immediately - don't do any history manipulation
  if (isGiftCardPay) {
    clearAll(ss)
    return
  }
  const justReturned = isLikelyFromStripeReferrer() || hasPayQuery
  if (!justReturned) return
  // If history isn't deep enough, we can't reliably skip the Stripe entry.
  if (typeof window.history === 'undefined' || window.history.length < 3) {
    clearAll(ss)
    return
  }
  // Step 1: store where we are now (the return page), jump back 2 entries to skip Stripe.
  const returnNow =
    router && router.currentRoute && router.currentRoute.fullPath ? router.currentRoute.fullPath
      : getCurrentFullPathFromHash()
  try {
    ss.setItem(KEY_FIX_PENDING, '1')
    ss.setItem(KEY_RETURN_TO, returnNow)
    ss.setItem(KEY_NAV_MODE, getPathOnly(from) === getPathOnly(returnNow) ? 'replace' : 'push')
  } catch (e) {
    clearAll(ss)
    return
  }
  try {
    window.history.go(-2)
  } catch (e) {
    clearAll(ss)
  }
}
